import axios from 'axios';
import { AuthV3ErrorClassification } from '@am3nd-org/pointcraft-types';

export type AuthOperationFailureKind =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'RATE_LIMIT'
  | 'SERVER'
  | 'AUTHENTICATION'
  | 'AUTHORIZATION'
  | 'DEVICE_REVOKED'
  | 'DEVICE_NOT_FOUND'
  | 'DEVICE_KEY_MISMATCH'
  | 'DATA'
  | 'UNKNOWN';

export interface AuthOperationFailure {
  kind: AuthOperationFailureKind;
  code?: string;
  httpStatus?: number;
  retryAfterMs?: number;
  supportReference?: string;
}

export class AuthOperationError extends Error implements AuthOperationFailure {
  readonly kind: AuthOperationFailureKind;

  readonly code?: string;

  readonly httpStatus?: number;

  readonly retryAfterMs?: number;

  readonly supportReference?: string;

  constructor(message: string, failure: AuthOperationFailure, options?: ErrorOptions) {
    super(message, options);
    this.name = 'AuthOperationError';
    this.kind = failure.kind;
    this.code = failure.code;
    this.httpStatus = failure.httpStatus;
    this.retryAfterMs = failure.retryAfterMs;
    this.supportReference = failure.supportReference;
  }
}

const TRANSIENT_FIREBASE_CODES = new Map<string, AuthOperationFailureKind>([
  ['auth/network-request-failed', 'NETWORK'],
  ['auth/timeout', 'TIMEOUT'],
  ['auth/too-many-requests', 'RATE_LIMIT'],
]);

const AUTHENTICATION_FIREBASE_CODES = new Set([
  'auth/id-token-expired',
  'auth/invalid-user-token',
  'auth/requires-recent-login',
  'auth/user-token-expired',
]);

const INVALID_CUSTOM_TOKEN_FIREBASE_CODES = new Set([
  'auth/invalid-custom-token',
  'auth/custom-token-mismatch',
]);

const parseRetryAfterMs = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : undefined;
};

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length ? value : undefined;
}

export function getFirebaseErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return readString((error as { code?: unknown }).code);
}

export function isDefinitivelyInvalidCustomToken(error: unknown): boolean {
  const code = getFirebaseErrorCode(error);
  return Boolean(code && INVALID_CUSTOM_TOKEN_FIREBASE_CODES.has(code));
}

export function classifyAuthOperationError(error: unknown): AuthOperationFailure {
  if (error instanceof AuthOperationError) return error;

  const firebaseCode = getFirebaseErrorCode(error);
  if (firebaseCode) {
    const transientKind = TRANSIENT_FIREBASE_CODES.get(firebaseCode);
    if (transientKind) return { kind: transientKind, code: firebaseCode };
    if (AUTHENTICATION_FIREBASE_CODES.has(firebaseCode)) {
      return { kind: 'AUTHENTICATION', code: firebaseCode };
    }
    // A disabled Firebase principal invalidates the current sign-in, but it is
    // not proof that the paired business-device identity was revoked. Only the
    // Auth V3 backend's explicit device classifications may destroy pairing.
    if (firebaseCode === 'auth/user-disabled') {
      return { kind: 'AUTHENTICATION', code: firebaseCode };
    }
    if (INVALID_CUSTOM_TOKEN_FIREBASE_CODES.has(firebaseCode)) {
      return { kind: 'AUTHENTICATION', code: firebaseCode };
    }
  }

  if (axios.isAxiosError(error)) {
    const payload =
      error.response?.data && typeof error.response.data === 'object'
        ? (error.response.data as { errorCode?: unknown; supportReference?: unknown })
        : undefined;
    const code = readString(payload?.errorCode);
    const supportReference = readString(payload?.supportReference);
    const httpStatus = error.response?.status;
    const retryAfterMs = parseRetryAfterMs(error.response?.headers?.['retry-after']);

    if (code === AuthV3ErrorClassification.DEVICE_REVOKED) {
      return { kind: 'DEVICE_REVOKED', code, httpStatus, supportReference };
    }
    if (code === AuthV3ErrorClassification.DEVICE_NOT_FOUND) {
      return { kind: 'DEVICE_NOT_FOUND', code, httpStatus, supportReference };
    }
    if (code === AuthV3ErrorClassification.DEVICE_KEY_MISMATCH) {
      return { kind: 'DEVICE_KEY_MISMATCH', code, httpStatus, supportReference };
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return { kind: 'TIMEOUT', code: error.code, httpStatus };
    }
    if (!error.response) {
      return { kind: 'NETWORK', code: error.code, httpStatus };
    }
    if (httpStatus === 401) return { kind: 'AUTHENTICATION', code, httpStatus };
    if (httpStatus === 403) return { kind: 'AUTHORIZATION', code, httpStatus };
    if (httpStatus === 408) return { kind: 'TIMEOUT', code, httpStatus };
    if (httpStatus === 429) {
      return { kind: 'RATE_LIMIT', code, httpStatus, retryAfterMs };
    }
    if ((httpStatus ?? 0) >= 500 || code === 'C-9858') {
      return { kind: 'SERVER', code, httpStatus, supportReference };
    }
    return { kind: 'DATA', code, httpStatus, supportReference };
  }

  if (error && typeof error === 'object') {
    const candidate = error as {
      errorCode?: unknown;
      httpStatus?: unknown;
      retryAfterMs?: unknown;
      supportReference?: unknown;
      isNetworkError?: unknown;
      isTimeout?: unknown;
      name?: unknown;
    };
    const code = readString(candidate.errorCode);
    const httpStatus =
      typeof candidate.httpStatus === 'number' ? candidate.httpStatus : undefined;
    const retryAfterMs =
      typeof candidate.retryAfterMs === 'number' ? candidate.retryAfterMs : undefined;
    const supportReference = readString(candidate.supportReference);

    if (code === AuthV3ErrorClassification.DEVICE_REVOKED) {
      return { kind: 'DEVICE_REVOKED', code, httpStatus, supportReference };
    }
    if (code === AuthV3ErrorClassification.DEVICE_NOT_FOUND) {
      return { kind: 'DEVICE_NOT_FOUND', code, httpStatus, supportReference };
    }
    if (code === AuthV3ErrorClassification.DEVICE_KEY_MISMATCH) {
      return { kind: 'DEVICE_KEY_MISMATCH', code, httpStatus, supportReference };
    }
    if (candidate.isTimeout || candidate.name === 'AbortError') {
      return { kind: 'TIMEOUT', code, httpStatus };
    }
    if (candidate.isNetworkError) return { kind: 'NETWORK', code, httpStatus };
    if (httpStatus === 401) return { kind: 'AUTHENTICATION', code, httpStatus };
    if (httpStatus === 403) return { kind: 'AUTHORIZATION', code, httpStatus };
    if (httpStatus === 408) return { kind: 'TIMEOUT', code, httpStatus };
    if (httpStatus === 429) return { kind: 'RATE_LIMIT', code, httpStatus, retryAfterMs };
    if ((httpStatus ?? 0) >= 500 || code === 'C-9858') {
      return { kind: 'SERVER', code, httpStatus, supportReference };
    }
  }

  return { kind: 'UNKNOWN' };
}

export function isTransientAuthOperationFailure(failure: AuthOperationFailure): boolean {
  return ['NETWORK', 'TIMEOUT', 'RATE_LIMIT', 'SERVER'].includes(failure.kind);
}

export function isDefinitiveDeviceFailure(failure: AuthOperationFailure): boolean {
  return ['DEVICE_REVOKED', 'DEVICE_NOT_FOUND', 'DEVICE_KEY_MISMATCH'].includes(failure.kind);
}
